import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3000);
  }, []);

  const success = useCallback((message) => addToast(message, 'success'), [addToast]);
  const error = useCallback((message) => addToast(message, 'error'), [addToast]);
  const info = useCallback((message) => addToast(message, 'info'), [addToast]);
  const warning = useCallback((message) => addToast(message, 'warning'), [addToast]);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      const id = Date.now() + Math.random();
      const handleConfirm = (result) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        resolve(result);
      };

      setToasts((prev) => [
        ...prev,
        { id, message, type: 'confirm', onConfirm: handleConfirm }
      ]);
    });
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ success, error, info, warning, confirm }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.filter(t => t.type !== 'confirm').map((toast) => (
          <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
      {toasts.filter(t => t.type === 'confirm').map((toast) => (
        <div key={toast.id} className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => toast.onConfirm(false)} />
          <Toast {...toast} onClose={() => removeToast(toast.id)} />
        </div>
      ))}
    </ToastContext.Provider>
  );
};

const Toast = ({ id, message, type, onClose, onConfirm }) => {
  const typeStyles = {
    success: 'bg-success-50 border-success-500 text-success-800',
    error: 'bg-danger-50 border-danger-500 text-danger-800',
    info: 'bg-primary-50 border-primary-500 text-primary-800',
    warning: 'bg-yellow-50 border-yellow-500 text-yellow-800',
  };

  const icons = {
    success: (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-success-500">
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 13l3 3 7-7" />
        </svg>
      </span>
    ),
    error: (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-danger-500">
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 9l-6 6M9 9l6 6" />
        </svg>
      </span>
    ),
    info: (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-500">
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
        </svg>
      </span>
    ),
    warning: (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400">
        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
        </svg>
      </span>
    ),
  };

  // Progress bar animation (3s)
  const [progress, setProgress] = React.useState(100);
  React.useEffect(() => {
    if (progress === 0) return;
    const interval = setInterval(() => {
      setProgress((p) => (p > 0 ? p - 2 : 0));
    }, 60);
    return () => clearInterval(interval);
  }, [progress]);

  if (type === 'confirm') {
    return (
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
        <div className="flex flex-col items-center mb-4">
          <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-danger-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/>
              <line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
          <p className="text-sm text-gray-600 text-center">{message}</p>
        </div>
        <div className="flex gap-3 justify-center">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            onClick={() => onConfirm(false)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Cancel
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 bg-danger-600 text-white rounded-md hover:bg-danger-700 transition-colors"
            onClick={() => onConfirm(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative min-w-[200px] rounded-md shadow-lg p-2 flex items-center gap-3 ${typeStyles[type]} animate-slide-in`}>
      <div className="flex-shrink-0">{icons[type]}</div>
      <div className="flex-1 text-sm font-medium">{message}</div>
      <button className="flex-shrink-0 text-xl leading-none hover:opacity-70 transition-opacity" onClick={onClose}>
        ×
      </button>
      {/* Progress Bar */}
      <div className="absolute left-0 bottom-0 h-1 rounded-b-md" style={{ width: '100%' }}>
        <div
          className={
            type === 'success' ? 'bg-success-500' :
            type === 'error' ? 'bg-danger-500' :
            type === 'warning' ? 'bg-yellow-400' :
            'bg-primary-500'
          }
          style={{ width: `${progress}%`, height: '100%', transition: 'width 0.06s linear' }}
        />
      </div>
    </div>
  );
};

export default Toast;
